import React, { useEffect, useState, useRef, useCallback } from "react"; 
import apiClient from "../utils/apiClient";
import { client } from '../sanity/sanityClient';
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { io } from "socket.io-client";
import styled from "styled-components";
import {
  allusersroute,
  contactbymessages,
  singleUserRoute,
  host,
  updatefcmtoken,
  markMessagesAsReadRoute,
} from "../utils/APIRoutes";
import { getCurrentToken } from '../firebase';
import ChatContainer from "../components/ChatContainer";
import Contacts from "../components/Contacts";
import CategorySwitch from "../components/CategorySwitch";
import Categories from "../components/Categories";
import UploadForm from "../components/UploadForm";
import ContactsMessaged from "../components/ContactsMessaged";
import {
  getCachedContacts, setCachedContacts,
  getCachedContactList, setCachedContactList,
  getCachedCategories, setCachedCategories,
  getSetting, setSetting,
} from '../db';
import useOnlineStatus from '../useOnlineStatus';

const currencyOptions = [
  { code: 'TZS', name: 'Tanzanian Shilling (TZS)' },
  { code: 'KES', name: 'Kenyan Shilling (KES)' },
  { code: 'UGX', name: 'Ugandan Shilling (UGX)' },
  { code: 'RWF', name: 'Rwandan Franc (RWF)' },
  { code: 'USD', name: 'US Dollar (USD)' },
  { code: 'ZAR', name: 'South African Rand (ZAR)' },
  { code: 'NGN', name: 'Nigerian Naira (NGN)' },
  { code: 'EUR', name: 'Euro (EUR)' }
];

export default function Chat() {
  const navigate = useNavigate();
  const location = useLocation();
  const { categoryName, userId } = useParams();
  const socket = useRef();

  const [contacts, setContacts] = useState([]);
  const [contactList, setContactList] = useState([]);
  const prevContactsLength = useRef(contacts.length);
  const [currentChat, setCurrentChat] = useState(undefined);
  const [currentUser, setCurrentUser] = useState(undefined);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [listSet, setListSet] = useState(true);
  const [categoryType, setCategoryType] = useState(true);
  const [showContacts, setShowContacts] = useState(false);
  const [traderProducts, setTraderProducts] = useState([]);
  const [isTrader, setIsTrader] = useState(false);
  const [nearBy, setNearBy] = useState(null);
  const [message, setMessage] = useState("Welcome to BridgenApp ");
  const [popDelete, setPopDelete] = useState(false);
  const [selectedCurrency, setSelectedCurrency] = useState(() => {
  return localStorage.getItem('selectedCurrency') || 'TZS';
});
  const [noProductsForCurrency, setNoProductsForCurrency] = useState(false);
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [query] = useState(["grocery", "beauty", "clothingandfashion", "electronics", "jewelryandaccessories", "fashion"]);

  const [showOfflineBanner, setShowOfflineBanner] = useState(false);

  useEffect(() => {
    if (contacts.length !== prevContactsLength.current) {
      setCachedContacts(contacts);
      prevContactsLength.current = contacts.length;
    }
  }, [contacts]);

  useEffect(() => {
    if (contactList.length) setCachedContactList(contactList);
  }, [contactList]);

  // Fetch current user on mount
  useEffect(() => {
    const fetchUserAndContacts = async () => {
      const userData = localStorage.getItem(process.env.REACT_APP_LOCALHOST_KEY);
      if (!userData) return;

      const parsedUser = JSON.parse(userData);
      setCurrentUser(parsedUser);

      if (!(await getSetting('selectedCurrency'))) {
        await setSetting('selectedCurrency', 'TZS');
      }
      setSelectedCurrency(await getSetting('selectedCurrency', 'TZS'));

      if (!(parsedUser.isGoogleUser || parsedUser.isAvatarImageSet)) {
        navigate('/setAvatar');
        return;
      }

      // 1. Instant paint from IndexedDB
      const [cachedContacts, cachedContactList] = await Promise.all([
        getCachedContacts(),
        getCachedContactList(),
      ]);
      if (cachedContacts.length) setContacts(cachedContacts);
      if (cachedContactList.length) setContactList(cachedContactList);

      // 2. Background refresh — silently no-ops if offline, keeping
      //    cached data on screen instead of clearing it.
      //
      //    SECURITY: swapped from `axios` to `apiClient` here (and
      //    everywhere else in this file). Under the new session-cookie
      //    backend, plain axios calls don't carry the auth cookie at
      //    all — every one of these would 401 without this change,
      //    regardless of whether the user is actually logged in.
      try {
        const [allUsersRes, messagedRes] = await Promise.all([
          apiClient.get(`${allusersroute}/${parsedUser._id}`),
          apiClient.get(`${contactbymessages}/${parsedUser._id}`),
        ]);

        let allUsersList = allUsersRes?.data;
        if (!Array.isArray(allUsersList)) allUsersList = allUsersList?.users || [];
        if (!Array.isArray(allUsersList)) allUsersList = [];

        let messagedList = messagedRes?.data;
        if (!Array.isArray(messagedList)) messagedList = messagedList?.contacts || [];
        if (!Array.isArray(messagedList)) messagedList = [];

        const uniqueMessagedContacts = messagedList.reduce((acc, current) => {
          if (current?._id && !acc.some((item) => item._id === current._id)) acc.push(current);
          return acc;
        }, []);

        setContacts(allUsersList);
        setContactList(uniqueMessagedContacts);
      } catch (err) {
        console.error('Network refresh failed (likely offline):', err?.message);
        // No setContacts([]) here — that's the old bug that erased the UI.
      }
    };

    fetchUserAndContacts();
  }, [navigate]);

  // Setup socket and FCM token
  useEffect(() => {
    if (currentUser) {
      // SECURITY: `withCredentials: true` makes the browser attach the
      // httpOnly auth cookie to the socket.io handshake request, the
      // same way apiClient does for regular HTTP calls. This is what
      // lets a socketAuth-style server middleware (see
      // middleware/socketAuth.js) verify who this connection actually
      // belongs to.
      //
      // The `emit('add-user', currentUser._id)` below is left in for
      // backward compatibility, but it must be treated as advisory
      // only on the server from now on — currentUser._id comes from a
      // client-editable localStorage object, not from anything
      // verified. The server should derive identity from the verified
      // cookie during the handshake (socket.userId), never from this
      // event's payload. Without that server-side change, anyone can
      // still claim to be any user simply by editing localStorage
      // before this line runs — connecting with credentials alone
      // doesn't close that gap by itself; the server has to stop
      // trusting the emitted id too.
      socket.current = io(host, { withCredentials: true });
      socket.current.emit('add-user', currentUser._id);

      getCurrentToken()
        .then((currentToken) => {
          if (currentToken) {
            updateFCMToken(currentUser._id, currentToken);
          }
        })
        .catch((error) => {
          console.error('Error fetching FCM token:', error);
        });
    }

    return () => {
      if (socket.current) {
        socket.current.disconnect();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  const updateFCMToken = async (userId, token) => {
    try {
      await apiClient.post(updatefcmtoken, { userId, token });
    } catch (error) {
      console.error('Error updating FCM token:', error);
    }
  };

  const updateContactMessaged = async () => {
    try {
      const response = await apiClient.get(`${contactbymessages}/${currentUser._id}`);

      let contacts = response.data;
      if (!Array.isArray(contacts)) contacts = contacts?.contacts || [];
      if (!Array.isArray(contacts)) contacts = [];

      const normalizedContacts = contacts.reduce((acc, current) => {
        if (current && current._id && !acc.some((item) => item._id === current._id)) {
          acc.push(current);
        }
        return acc;
      }, []);

      const sortedContacts = [...normalizedContacts].sort(
        (a, b) =>
          new Date(b.updatedAt || b.lastMessageTime || 0) -
          new Date(a.updatedAt || a.lastMessageTime || 0)
      );

      setContactList(sortedContacts);

      // Write-through to IndexedDB instead of localStorage — this is
      // what lets the contact list survive a refresh while offline.
      await setCachedContactList(normalizedContacts);
    } catch (error) {
      console.error("Error fetching contacts (likely offline):", error.message);
    }
  };

  const updateConversation = (contact, action = "message") => {
    setContactList(prev => {
      let updated = [...prev];
      const index = updated.findIndex(c => c._id === contact._id);

      if (action === "remove") {
        if (index !== -1) updated.splice(index, 1);
        return updated;
      }

      const now = new Date().toISOString();

      if (index !== -1) {
        updated[index] = { ...updated[index], ...contact, lastMessageTime: now, updatedAt: now };
        const [existing] = updated.splice(index, 1);
        updated.unshift(existing);
      } else {
        updated.unshift({
          ...contact,
          lastMessageTime: now,
          updatedAt: now,
          unreadCount: action === "receive" ? 1 : 0,
        });
      }

      return updated;
    });
  };

  const changeCategory = () => {
    setCategoryType(!categoryType);
  };

  const handleCurrencyChange = async (e) => {
    const newCurrency = e.target.value;
    setSelectedCurrency(newCurrency);
    localStorage.setItem("selectedCurrency", newCurrency);
    setPage(1);
    await fetchCategories(1, false);
  };

  // Main fetch function with pagination support, random ordering,
  // currency specific and even-number enforcement. Talks to Sanity
  // directly (client.fetch), same as before — not part of this pass's
  // auth changes, and the queries here already use bound GROQ params
  // ($activeQuery, $currency, etc.) rather than string interpolation,
  // so there's no injection concern to fix.
  const fetchCategories = useCallback(async (page = 1, isLoadMore = false) => {
    const currency = localStorage.getItem('selectedCurrency') || 'TZS';
    const activeQuery = categoryName ? [categoryName] : query;

    if (isLoadMore) {
      setIsFetchingMore(true);
    } else {
      setIsLoading(true);
      setNoProductsForCurrency(false);

      try {
        const cached = await getCachedCategories(currency, activeQuery);
        if (cached.length) setCategories(cached);
        else setCategories([]);
      } catch (cacheErr) {
        console.error('Category cache read failed (continuing to network fetch):', cacheErr);
      }

      setPage(1);
      setHasMore(true);
    }

    try {
      const limit = 40;
      const skip = (page - 1) * limit;

      let data;

      if (nearBy) {
        const { latitude, longitude } = nearBy;

        const geoQuery = `
          *[
            _type in $activeQuery
            && defined(geolocation)
            && geo::distance(geolocation, $currentLocation) <= $distance
            && currency == $currency
          ]
          | order(geo::distance(geolocation, $currentLocation) asc)
          [${skip}...${skip + limit}]
          {
            ...,
            "distance": geo::distance(geolocation, $currentLocation) / 1000
          }
        `;

        const params = {
          activeQuery,
          currentLocation: { lat: latitude, lng: longitude },
          distance: 1000,
          currency,
        };

        data = await client.fetch(geoQuery, params);
      } else {
        const productQuery = `
          *[_type in $activeQuery && currency == $currency]
          [${skip}...${skip + limit}]
        `;

        data = await client.fetch(productQuery, { activeQuery, currency });
      }

      const shuffleArray = (array) => {
        const newArray = [...array];
        for (let i = newArray.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
        }
        return newArray;
      };

      let shuffledData = shuffleArray(data);

      let uniqueData = shuffledData.reduce((acc, current) => {
        const x = acc.find(item => item._id === current._id);
        if (!x) return acc.concat([current]);
        return acc;
      }, []);

      if (page > 1 && uniqueData.length > 0) {
        if (uniqueData.length % 2 !== 0) {
          uniqueData = uniqueData.slice(0, -1);
        }
      }

      if (uniqueData.length === 0) {
        setHasMore(false);

        if (!isLoadMore) {
          setCategories([]);
          setNoProductsForCurrency(true);
        }
      } else {
        setNoProductsForCurrency(false);

        if (isLoadMore) {
          setCategories(prev => {
            const combined = [...prev, ...uniqueData];
            const deduped = combined.reduce((acc, current) => {
              const x = acc.find(item => item._id === current._id);
              if (!x) return acc.concat([current]);
              return acc;
            }, []);

            if (page > 1 && deduped.length % 2 !== 0) {
              return deduped.slice(0, -1);
            }
            return deduped;
          });
        } else {
          setCategories(uniqueData);
        }
      }

      try {
        await setCachedCategories(uniqueData);
      } catch (cacheErr) {
        console.error('Category cache write failed (non-fatal):', cacheErr);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
    } finally {
      if (isLoadMore) {
        setIsFetchingMore(false);
      } else {
        setIsLoading(false);
      }
    }
  }, [categoryName, query, nearBy]);

  useEffect(() => {
    fetchCategories(1, false);
  }, [fetchCategories]);

  const isOnline = useOnlineStatus(() => {
    if (currentUser) updateContactMessaged();
    fetchCategories(1, false);
  });

  useEffect(() => {
    if (!isOnline) {
      setShowOfflineBanner(true);
      const timer = setTimeout(() => setShowOfflineBanner(false), 3000);
      return () => clearTimeout(timer);
    } else {
      setShowOfflineBanner(false);
    }
  }, [isOnline]);

  const loadMoreProducts = useCallback(() => {
    if (!isFetchingMore && hasMore && categories.length >= 40) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchCategories(nextPage, true);
    }
  }, [isFetchingMore, hasMore, page, categories.length, fetchCategories]);

  const markAsRead = async (contactId) => {
    // Instant UI update
    setContactList(prev =>
      prev.map(contact =>
        contact._id === contactId ? { ...contact, unreadCount: 0 } : contact
      )
    );

    // SECURITY: sends `userId: currentUser._id` in the body — same
    // pattern that was fixed across every userRoutes.js endpoint
    // (requireSelfOrAdmin verifying the body's userId against the
    // session). This message-routes endpoint hasn't had that applied
    // yet; see the note on markMessagesAsReadRouteRoute in APIRoutes.js and
    // this turn's summary for how to close it. The call itself is
    // otherwise unchanged.
    try {
      await apiClient.post(markMessagesAsReadRoute, {
        userId: currentUser._id,
        contactId,
      });
    } catch (err) {
      console.error("Failed to mark messages as read", err);
      updateContactMessaged();
    }
  };

  const handleNearbyClick = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setNearBy({ latitude: position.coords.latitude, longitude: position.coords.longitude });
        },
        (error) => {
          console.error("Error getting geolocation:", error);
          alert("Unable to fetch your location. Please allow location access.");
        }
      );
    } else {
      alert("Geolocation is not supported by your browser.");
    }
  };

  const fetchTraderProducts = async (traderId) => {
    setIsTrader(!isTrader);
    try {
      const productQuery = `*[_type in [ "clothingandfashion", "electronics", "arts", "fashion", "salon", "grocery", "beauty", "crops", "livestock", "servicesandinstitutions", "energyandfuel",
       "toys", "sports", "kitchen", "hotels", "hardware", "books", "estate", "foodandbeverages", "agritech", "giftoptions", "cuisines", "eventsandnightclubs",
        "homeandfurnitures", "vehicles", "jewelryandaccessories", "medical", "useditems", "construction", "kidsandtoys", "hospitality", "booksandstationery"]
        && user == $traderId]`;
      const result = await client.fetch(productQuery, { traderId });
      setTraderProducts(result);
    } catch (error) {
      console.error("Error fetching products:", error);
    }
  };

  const getISO24HoursAgo = () => {
    const date = new Date();
    date.setHours(date.getHours() - 24);
    return date.toISOString();
  };

  const loadArrivals = async () => {
    setIsLoading(true);

    try {
      const currency = localStorage.getItem("selectedCurrency") || "TZS";
      const since = getISO24HoursAgo();

      const queryArrivals = `
        *[
          _type in [
            "clothingandfashion", "electronics", "arts", "fashion", "salon", "eventsandnightclubs",
            "grocery", "beauty", "crops", "toys", "sports", "kitchen", "cuisines", "energyandfuel",
            "hotels", "homeandfurniture", "hardware", "books", "estate", "booksandstationery",
            "foodandbeverages", "agritech", "giftoptions", "homeandfurnitures",
            "vehicles", "jewelryandaccessories", "medical", "useditems", "servicesandinstitutions",
            "construction", "kidsandtoys", "hospitality"
          ]
          && _createdAt >= $since
          && currency == $currency
        ]
        | order(_createdAt desc)
      `;

      const result = await client.fetch(queryArrivals, { since, currency });
      setCategories(result);
    } catch (error) {
      console.error("Error fetching arrivals:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (location.state?.productImage) {
      setSelectedProduct(location.state.productImage);
      navigate(location.pathname, { replace: true, state: {} });
    } else {
      const savedProduct = localStorage.getItem('currentProduct');
      if (savedProduct) {
        const productData = JSON.parse(savedProduct);
        setSelectedProduct(productData.image);
        localStorage.removeItem('currentProduct');
      }
    }
  }, [location.state, location.pathname, navigate]);

  useEffect(() => {
    const initializeChat = async () => {
      try {
        if (!userId) {
          setMessage("Welcome to BridgenApp");
          return;
        }

        if (userId === currentUser?._id) {
          setCurrentChat(undefined);
          setMessage("You clicked your own item!");
          return;
        }

        const matchedContact = contacts.find((contact) => contact._id === userId);

        if (matchedContact) {
          setCurrentChat(matchedContact);
          setMessage("");
          return;
        }

        const { data } = await apiClient.get(`${singleUserRoute}/${userId}`);

        if (data.success && data.user) {
          setCurrentChat(data.user);

          setContacts((prev) => {
            const exists = prev.some((c) => c._id === data.user._id);
            if (exists) return prev;
            return [data.user, ...prev];
          });

          setMessage("");
        } else {
          setCurrentChat(undefined);
          setMessage("unregistered");
        }
      } catch (error) {
        console.error("Error initializing chat:", error);
        setCurrentChat(undefined);
        setMessage("unregistered");
      }
    };

    initializeChat();
  }, [userId, contacts, currentUser]);

  const handleChatChange = (chat) => {
    setCurrentChat(chat);
  };

  const handleProductSelect = (product) => {
    setSelectedProduct(product);
  };

  const handleProductDeselect = () => {
    setSelectedProduct(null);
  };

  const handleBack = () => {
    setCurrentChat(undefined);
  };

  const showCntcts = () => {
    setShowContacts(!showContacts);
  };

  return (
    <Container currentChat={currentChat}>
      {showOfflineBanner && (
        <div style={{ background: '#444', color: 'white', textAlign: 'center', fontSize: '12px', padding: '4px' }}>
          You're offline — showing saved content.
        </div>
      )}

      <div className="toLet" style={{ width: "101%", height: "40px", top: '0rem', paddingBottom: '5px', borderRadius:'5px' }}>
        <ContactsMessaged 
          setListSet={setListSet} 
          setPopDelete={setPopDelete} 
          fetchTraderProducts={fetchTraderProducts} 
          contactList={contactList} 
          changeChat={handleChatChange} 
          showCntcts={showCntcts} 
          currentChat={currentChat}
          socket={socket}
          markAsRead={markAsRead}
        />
      </div>
      <div>
        <div className="container">
          <div className='contacts-container'>
            {showContacts && (
              listSet ? (
                <CategorySwitch showCntcts={showCntcts} />
              ) : (
                <Contacts contacts={contacts} changeChat={handleChatChange} setListSet={setListSet} showCntcts={showCntcts} />
              )
            )}
          </div>

          {!currentUser || currentChat === undefined ? (
            categoryType ? (
              <Categories
                categories={categories}
                currentUser={currentUser}
                isLoading={isLoading}
                isFetchingMore={isFetchingMore}
                hasMore={hasMore}
                loadMoreProducts={loadMoreProducts}
                changeCategory={changeCategory}
                showCntcts={showCntcts}
                handleNearbyClick={handleNearbyClick}
                fetchTraderProducts={fetchTraderProducts}
                nearBy={nearBy}
                isTrader={isTrader}
                traderProducts={traderProducts}
                message={message}
                popDelete={popDelete}
                setPopDelete={setPopDelete}
                handleCurrencyChange={handleCurrencyChange}
                selectedCurrency={selectedCurrency}
                currencyOptions={currencyOptions}
                noProductsForCurrency={noProductsForCurrency}
                loadArrivals={loadArrivals}
              />
            ) : (
              <UploadForm />
            )
          ) : (
            <ChatContainer
              currentChat={currentChat}
              currentUser={currentUser}
                socket={socket}
                selectedProduct={selectedProduct}
                productDeselect={handleProductDeselect}
                handleBack={handleBack}
                fetchTraderProducts={fetchTraderProducts}
                updateConversation={updateConversation}
                categories={categories}
                isLoading={isLoading}
                isTrader={isTrader}
                traderProducts={traderProducts}
                onSelectedProduct={handleProductSelect}
            />
          )}
          </div>
      </div>
    </Container>
  );
}

const Container = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  border-radius: 10px;
  gap: 1rem;
  align-items: center;
  background-color: #1f2937;

  .container {
  position: relative;
  height: calc(100vh - 10vh);
  width: 100vw;
  background-color: white;
  display: ${(props) => (props.currentChat ? "grid" : "flex")};
  grid-template-columns: ${(props) =>
    props.currentChat ? "0% 100%" : "none"};
}
    
    @media screen and (min-width: 720px) and (max-width: 1080px) {
      grid-template-columns: 35% 65%;
    }
    
    @media screen and (max-width: 768px) {
      grid-template-columns: ${(props) => (props.currentChat ? "0% 50% 50%" : "none")};
    }
  

  .contacts-container {
    position: ${(props) => (props.currentChat ? "relative" : "absolute")};
    z-index: ${(props) => (props.currentChat ? "auto" : "3")};
    top: 0;
    left: 0;
    width: 30%;
  }
`;